/**
 * Issue Card — War Room Phase C.3
 *
 * Expandable card showing one disputed issue with:
 * - Agent positions (values, confidence, reasoning)
 * - Resolution paths with dollar impact
 * - Human inputs received
 * - Inline human input form (collapsible)
 */

import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { displayText } from '@/lib/displayText';
import {
  ChevronDown,
  ChevronUp,
  DollarSign,
  Users,
  AlertCircle,
  CheckCircle,
  MessageSquare,
} from 'lucide-react';
import {
  type DisputedIssue,
  type AgentPosition,
  getAgentDisplayName,
  getAgentColor,
  formatCurrency,
} from '@/services/deliberationApi';
import { HumanInputForm } from './HumanInputForm';

// =============================================================================
// TYPES
// =============================================================================

export interface IssueCardProps {
  issue: DisputedIssue;
  reportId: string;
  onInputSubmitted?: () => void;
}

// =============================================================================
// HELPERS
// =============================================================================

function getStatusBadge(status: string): { label: string; style: React.CSSProperties } {
  switch (status) {
    case 'resolved':
      return { label: 'Resolved', style: { backgroundColor: 'transparent', color: 'var(--color-verified)' } };
    case 'human_override':
      return { label: 'Human Override', style: { backgroundColor: 'transparent', color: 'var(--color-needs-human)' } };
    default:
      // Status is not an action — hueless outline (K3: the needs-human signal is
      // carried by the row's alert icon + the MIDDLE-ADOPTED marker, not a wash).
      return { label: 'Open', style: { backgroundColor: 'transparent', color: 'var(--color-text-secondary)' } };
  }
}

function getAgentBg(agentName: string): React.CSSProperties {
  const color = getAgentColor(agentName);
  const map: Record<string, React.CSSProperties> = {
    blue: { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' },
    purple: { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' },
    amber: { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' },
    green: { backgroundColor: 'transparent', borderColor: 'var(--color-verified)' },
    gray: { backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' },
  };
  return map[color] || map.gray;
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function AgentPositionRow({
  agentName,
  position,
}: {
  agentName: string;
  position: AgentPosition;
}) {
  return (
    <div className="rounded-lg border p-3" style={getAgentBg(agentName)}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
          {getAgentDisplayName(agentName)}
        </span>
        <div className="flex items-center gap-2">
          {position.value && (
            <span className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>
              {formatCurrency(position.value)}
            </span>
          )}
          {position.confidence !== null && (
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {Math.round(position.confidence * 100)}%
            </span>
          )}
        </div>
      </div>
      <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{displayText(position.reasoning)}</p>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function IssueCard({ issue, reportId, onInputSubmitted }: IssueCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [showInputForm, setShowInputForm] = useState(false);

  const statusBadge = getStatusBadge(issue.status);
  const agentEntries = Object.entries(issue.agent_positions);
  const hasDisagreement = agentEntries.length > 1;

  // Calculate total impact from resolution paths
  const totalImpact = issue.resolution_paths.reduce((sum, rp) => {
    return sum + (rp.impact_value ? parseFloat(rp.impact_value) : 0);
  }, 0);

  return (
    <div className="rounded-xl shadow-lg overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-4 transition-colors"
      >
        <div className="flex items-center gap-3">
          {hasDisagreement ? (
            <AlertCircle className="h-5 w-5" style={{ color: 'var(--color-brand)' }} />
          ) : (
            <CheckCircle className="h-5 w-5" style={{ color: 'var(--color-verified)' }} />
          )}
          <div className="text-left">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{issue.title}</h3>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{issue.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" style={statusBadge.style}>
            {statusBadge.label}
          </Badge>
          {agentEntries.length > 0 && (
            <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
              <Users className="h-3 w-3" />
              <span>{agentEntries.length}</span>
            </div>
          )}
          {totalImpact > 0 && (
            <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
              <DollarSign className="h-3 w-3" />
              <span>{formatCurrency(String(totalImpact))}</span>
            </div>
          )}
          {expanded ? (
            <ChevronUp className="h-4 w-4" style={{ color: 'var(--color-text-dim)' }} />
          ) : (
            <ChevronDown className="h-4 w-4" style={{ color: 'var(--color-text-dim)' }} />
          )}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t px-5 pb-4 space-y-4" style={{ borderColor: 'var(--color-border-sub)' }}>
          {/* Agent Positions */}
          {agentEntries.length > 0 && (
            <div className="pt-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--color-text-label)' }}>
                Agent Positions
              </h4>
              <div className="space-y-2">
                {agentEntries.map(([agentName, position]) => (
                  <AgentPositionRow
                    key={agentName}
                    agentName={agentName}
                    position={position}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Resolution Paths */}
          {issue.resolution_paths.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--color-text-label)' }}>
                Resolution Paths
              </h4>
              <div className="space-y-2">
                {issue.resolution_paths.map((rp, idx) => (
                  <div
                    key={idx}
                    className="flex items-start justify-between p-3 rounded-lg border"
                    style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}
                  >
                    <div className="flex-1">
                      <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{rp.action}</p>
                      <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                        Assigned to: <span className="font-medium">{rp.responsible_party}</span>
                        {rp.source_agent && (
                          <> · Suggested by {getAgentDisplayName(rp.source_agent)}</>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      {rp.impact_value && (
                        <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                          {formatCurrency(rp.impact_value)}
                        </span>
                      )}
                      <Badge
                        variant="outline"
                        style={
                          rp.priority === 'critical'
                            ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                            : rp.priority === 'high'
                            ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                            : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }
                        }
                      >
                        {rp.priority}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Human Inputs */}
          {issue.human_inputs.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--color-text-label)' }}>
                Human Inputs
              </h4>
              <div className="space-y-2">
                {issue.human_inputs.map((hi, idx) => (
                  <div
                    key={idx}
                    className="p-3 rounded-lg border"
                    style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <MessageSquare className="h-3 w-3" style={{ color: 'var(--color-verified)' }} />
                      <span className="text-xs font-medium" style={{ color: 'var(--color-verified)' }}>
                        {getAgentDisplayName(hi.agent_name)}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--color-verified)' }}>
                        {new Date(hi.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{displayText(hi.tool_result_summary)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Input Button / Form */}
          {issue.status === 'open' && (
            <div className="pt-2">
              {!showInputForm ? (
                <button
                  onClick={() => setShowInputForm(true)}
                  className="flex items-center gap-2 text-sm font-medium"
                  style={{ color: 'var(--color-needs-human-text)' }}
                >
                  <MessageSquare className="h-4 w-4" />
                  Add Expert Input
                </button>
              ) : (
                <HumanInputForm
                  reportId={reportId}
                  issueTag={issue.tag}
                  onSubmitted={() => {
                    setShowInputForm(false);
                    onInputSubmitted?.();
                  }}
                  onCancel={() => setShowInputForm(false)}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default IssueCard;
